import { cn } from "@bcc/lib/utils";
import type { PurchaseOrderStatus } from "@shared/types";
import { useTranslation } from "react-i18next";

const TIMELINE_STAGES = ["WaitingToBeFulfilled", "Fulfilled", "Shipped", "Delivered"] as const;

type TimelineStage = (typeof TIMELINE_STAGES)[number];

const STAGE_LABEL_KEYS: Record<TimelineStage, string> = {
  WaitingToBeFulfilled: "admin.crossBorder.timeline.ordered",
  Fulfilled: "admin.crossBorder.timeline.fulfilled",
  Shipped: "admin.crossBorder.timeline.shipped",
  Delivered: "admin.crossBorder.timeline.delivered",
};

const STATUS_TO_STAGE_INDEX: Partial<Record<PurchaseOrderStatus, number>> = {
  WaitingToBeFulfilled: 0,
  Publishing: 0,
  Fulfilled: 1,
  PartiallyFulfilled: 1,
  Shipped: 2,
  PartiallyShipped: 2,
  Delivered: 3,
  PartiallyDelivered: 3,
};

interface CrossBorderTimelineProps {
  readonly status?: PurchaseOrderStatus;
  readonly testId?: string;
}

export function CrossBorderTimeline({ status, testId }: CrossBorderTimelineProps) {
  const { t } = useTranslation();
  const isError = status && !(status in STATUS_TO_STAGE_INDEX);
  const currentStageIndex = status ? (STATUS_TO_STAGE_INDEX[status] ?? -1) : -1;

  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {TIMELINE_STAGES.map((stage, index) => {
        const isCompleted = index <= currentStageIndex;
        const isCurrent = index === currentStageIndex;
        const translated = t(STAGE_LABEL_KEYS[stage]);
        const label = typeof translated === "string" ? translated : JSON.stringify(translated);

        return (
          <div key={stage} className="flex items-center gap-1">
            <div
              className={cn(
                "h-2 w-2 rounded-full",
                isError && "bg-destructive",
                !isError && isCompleted && "bg-green-500",
                !isError && !isCompleted && "bg-muted",
                isCurrent && !isError && "ring-2 ring-green-500/30"
              )}
              title={label}
              aria-label={label}
            />
            {index < TIMELINE_STAGES.length - 1 && (
              <div className={cn("h-0.5 w-3", !isError && index < currentStageIndex ? "bg-green-500" : "bg-muted")} />
            )}
          </div>
        );
      })}
    </div>
  );
}
